import type { ComponentProps } from 'react'
import { useMemo } from 'react'
import { useIntl } from 'react-intl'

import MarkdownRender from 'LuxKit/MarkdownRender'
import Heading from 'LuxKit/Typography/Heading'
import Group from 'components/utils/Group'

import type { RoomPackageAmenityGroup } from './AccommodationAmenitiesList'
import AccommodationAmenitiesList, {
  extractBedbankRoomPackageAmenityGroups,
  extractHotelRoomPackageAmenityGroups,
  extractPropertyRoomPackageAmenityGroups,
} from './AccommodationAmenitiesList'
import { mapRoomPackage } from './roomPackageUtils'
import AccommodationExtraGuestPoliciesTable from './AccommodationExtraGuestPoliciesTable'
import AccommodationMaximumRoomCapacities from './AccommodationMaximumRoomCapacities'
import AccommodationPackageInclusions from './AccommodationPackageInclusions'
import AccommodationBeddingDetails from './AccommodationBeddingDetails'
import AccommodationAdditionalPolicyNotes from './AccommodationAdditionalPolicyNotes'

export interface IconTextItem {
  text: string
  icon?: string
}

// TODO: Derive from svc-offer contract later
export interface ExtraGuestPolicyItem {
  guest_type_label: string
  age_range: string
  fees: {
    guest_price_per_night: string
    fee_payable_to: string
  }
  inclusions: Array<IconTextItem>
  extra_bedding: string
}

export enum ExtraGuestTypeLabel {
  Children = 'children',
  Infants = 'infants',
}

export type ExtraGuestGeneratedPolicies = {
  extraGuestGeneratedPolicyItems: Array<ExtraGuestPolicyItem>,
  maximumRoomCapacities: Array<string>
  packageInclusions: Array<string>
  beddingConfiguration?: string
  beddingConfigurationExtras: Array<IconTextItem>
  additionalPolicyNotes: Array<string>
}

interface RoomCapacityObject {
  adults: number
  children: number
  infants: number
  teenagers: number
}

function isRoomCapacityObject(value: unknown): value is RoomCapacityObject {
  return typeof value === 'object' && value !== null && 'adults' in value
}

function normalizeBeddingConfiguration(value: unknown): string | undefined {
  if (typeof value === 'string') return value
  if (typeof value === 'object' && value !== null && 'bedding' in value) {
    const bedding = (value as { bedding?: unknown }).bedding
    return typeof bedding === 'string' ? bedding : undefined
  }
  return undefined
}

function mergeCapacityEntries(items: Array<RoomCapacityObject>): Array<string> {
  const groups = new Map<string, { adults: number; teenagers: number; total: number; hasChildren: boolean; hasInfants: boolean }>()

  for (const item of items) {
    const total = item.children + item.infants
    const key = `${item.adults}-${item.teenagers}-${total}`
    const existing = groups.get(key)
    if (existing) {
      existing.hasChildren = existing.hasChildren || item.children > 0
      existing.hasInfants = existing.hasInfants || item.infants > 0
    } else {
      groups.set(key, {
        adults: item.adults,
        teenagers: item.teenagers,
        total,
        hasChildren: item.children > 0,
        hasInfants: item.infants > 0,
      })
    }
  }

  return Array.from(groups.values()).map(({ adults, teenagers, total, hasChildren, hasInfants }) => {
    const parts: Array<string> = []
    if (adults > 0) parts.push(`${adults} ${adults === 1 ? 'adult' : 'adults'}`)
    if (teenagers > 0) parts.push(`${teenagers} ${teenagers === 1 ? 'teenager' : 'teenagers'}`)
    if (total > 0) {
      if (hasChildren && hasInfants) {
        parts.push(`${total} ${total === 1 ? 'child/infant' : 'children/infants'}`)
      } else if (hasChildren) {
        parts.push(`${total} ${total === 1 ? 'child' : 'children'}`)
      } else if (hasInfants) {
        parts.push(`${total} ${total === 1 ? 'infant' : 'infants'}`)
      }
    }
    return parts.join(', ')
  })
}

function parseExtraGuestGeneratedPolicies(policyDescription?: string): ExtraGuestGeneratedPolicies | undefined {
  if (!policyDescription) return undefined

  try {
    const parsed = JSON.parse(policyDescription)
    if (
      Array.isArray(parsed?.extraGuestGeneratedPolicyItems) &&
      Array.isArray(parsed?.maximumRoomCapacities) &&
      Array.isArray(parsed?.packageInclusions) &&
      Array.isArray(parsed?.additionalPolicyNotes)
    ) {
      return {
        ...parsed,
        maximumRoomCapacities: isRoomCapacityObject(parsed.maximumRoomCapacities[0])
          ? mergeCapacityEntries(parsed.maximumRoomCapacities)
          : parsed.maximumRoomCapacities,
        packageInclusions: isRoomCapacityObject(parsed.packageInclusions[0])
          ? mergeCapacityEntries(parsed.packageInclusions)
          : parsed.packageInclusions,
        beddingConfiguration: normalizeBeddingConfiguration(parsed.beddingConfiguration),
        beddingConfigurationExtras: Array.isArray(parsed.beddingConfigurationExtras)
          ? parsed.beddingConfigurationExtras.map((item: string | { bedding: string; icon?: string }): IconTextItem =>
              typeof item === 'string' ? { text: item } : { text: item.bedding, icon: item.icon || undefined },
            )
          : [],
        extraGuestGeneratedPolicyItems: parsed.extraGuestGeneratedPolicyItems.map(
          (item: Record<string, unknown> & { inclusions: Array<string | { inclusion: string; icon?: string }> }) => ({
            ...item,
            inclusions: item.inclusions.map((inc: string | { inclusion: string; icon?: string }): IconTextItem =>
              typeof inc === 'string' ? { text: inc } : { text: inc.inclusion, icon: inc.icon || undefined },
            ),
          }),
        ),
      }
    }
    return undefined
  } catch {
    return undefined
  }
}

interface RoomPackageDetails {
  amenityGroups: Array<RoomPackageAmenityGroup>
  policyDescription?: string
  beds?: string
}

function extractPropertyRoomPackageAmenitiesAndPoliciesDetails(roomPackage: App.Property.Room, orWord: string): RoomPackageDetails {
  return {
    amenityGroups: extractPropertyRoomPackageAmenityGroups(roomPackage),
    policyDescription: roomPackage.policy,
    beds: roomPackage.bedGroupDescriptions.join(` ${orWord} `) || undefined,
  }
}

function extractHotelRoomPackageAmenitiesAndPoliciesDetails(roomPackage: App.HotelPackage, orWord: string): RoomPackageDetails {
  return {
    amenityGroups: extractHotelRoomPackageAmenityGroups(roomPackage),
    policyDescription: roomPackage.roomPolicyDescription,
    beds: (roomPackage.roomType.bedGroups ?? []).map((b) => b.description).join(` ${orWord} `),
  }
}

function extractBedbankRoomPackageAmenitiesAndPoliciesDetails(roomPackage: App.BedbankPackage): RoomPackageDetails {
  const features = new Map(roomPackage.features.map((f) => ([f.id, f.text])))

  return {
    amenityGroups: extractBedbankRoomPackageAmenityGroups(roomPackage),
    policyDescription: undefined,
    beds: features.get('bed'),
  }
}

interface Props {
  roomPackage: App.Property.Room | App.HotelPackage | App.BedbankPackage
  headingVariant?: ComponentProps<typeof Heading>['variant']
  policyFontSize?: ComponentProps<typeof MarkdownRender>['fontSize']
}

function AccommodationAmenitiesAndPoliciesContent(props: Props) {
  const {
    roomPackage,
    headingVariant = 'heading5',
    policyFontSize,
  } = props

  const intl = useIntl()

  const orWord = intl.formatMessage({
    id: 'GzXJi62SQuqpWHf7Jmwj',
    defaultMessage: 'OR',
    description: 'Separator between alternative bed group options (e.g. "1 King Bed OR 2 Single Beds")',
  })

  const details = useMemo(() => {
    return mapRoomPackage(
      roomPackage,
      (room) => extractPropertyRoomPackageAmenitiesAndPoliciesDetails(room, orWord),
      (pkg) => extractHotelRoomPackageAmenitiesAndPoliciesDetails(pkg, orWord),
      extractBedbankRoomPackageAmenitiesAndPoliciesDetails,
    )
  }, [roomPackage, orWord])

  const extraGuestGeneratedPolicies = useMemo(() => {
    return parseExtraGuestGeneratedPolicies(details.policyDescription)
  }, [details.policyDescription])

  const roomAmenitiesHeading = intl.formatMessage({
    id: 'O0QKcNjGpIKeLndM3utX',
    defaultMessage: 'Room Amenities',
    description: 'Heading for the room amenities section within the room details content.',
  })

  const guestPolicyHeading = intl.formatMessage({
    id: 'iml0NoUKr2xKRNWRSyo5',
    defaultMessage: 'Guest Policy',
    description: 'Heading for the guest policy section within the room details content.',
  })

  const roomPoliciesHeading = intl.formatMessage({
    id: '_IjRrOdK6E3y0RVEFVhr',
    defaultMessage: 'Room Policies',
    description: 'Heading for the room policies section within the room details content.',
  })

  const hasNewPolicyUI = !!extraGuestGeneratedPolicies
  const hasFallbackPolicy = !hasNewPolicyUI && !!details.policyDescription

  return <>
    {!!details.amenityGroups.length && <Group direction="vertical" gap={12}>
      <Heading variant={headingVariant}>{roomAmenitiesHeading}</Heading>
      <AccommodationAmenitiesList amenities={details.amenityGroups} />
    </Group>}
    {hasNewPolicyUI && <Group direction="vertical" gap={12}>
      <Heading variant={headingVariant}>{guestPolicyHeading}</Heading>
      {extraGuestGeneratedPolicies.maximumRoomCapacities.length > 0 && <AccommodationMaximumRoomCapacities maximumRoomCapacities={extraGuestGeneratedPolicies.maximumRoomCapacities} />}
      {extraGuestGeneratedPolicies.packageInclusions.length > 0 && <AccommodationPackageInclusions packageInclusions={extraGuestGeneratedPolicies.packageInclusions} />}
      {extraGuestGeneratedPolicies.extraGuestGeneratedPolicyItems.length > 0 && <AccommodationExtraGuestPoliciesTable extraGuestGeneratedPolicyItems={extraGuestGeneratedPolicies.extraGuestGeneratedPolicyItems} />}
      {(extraGuestGeneratedPolicies.beddingConfiguration || details.beds) && <AccommodationBeddingDetails beddingDetails={extraGuestGeneratedPolicies.beddingConfiguration ?? details.beds!} extraGuestGeneratedPolicies={extraGuestGeneratedPolicies} />}
      {extraGuestGeneratedPolicies.additionalPolicyNotes.length > 0 && <AccommodationAdditionalPolicyNotes additionalPolicyNotes={extraGuestGeneratedPolicies.additionalPolicyNotes} />}
    </Group>}
    {hasFallbackPolicy && <Group direction="vertical" gap={12}>
      <Heading variant={headingVariant}>{roomPoliciesHeading}</Heading>
      <MarkdownRender content={details.policyDescription!} fontSize={policyFontSize} type="compact" />
    </Group>}
  </>
}

export default AccommodationAmenitiesAndPoliciesContent
